import _ from "lodash";

/**
 * Joins multiple label parts into a single comma-separated string, filtering out falsy values.
 * @param  {...string} parts - Label parts to combine
 * @returns {string} Comma-separated label string
 */
export const joinLabels = (...parts) => parts.filter(Boolean).join(", ");

/**
 * Handles keydown events for Enter or Space keys and triggers a callback function.
 *
 * @param {KeyboardEvent} event - The keyboard event object.
 * @param {Function} cb - The callback function to be executed when Enter or Space is pressed.
 * @returns {null|void} Returns null if the event is not provided.
 */
export const onEnterOrSpaceKeyDown = (
  event,
  cb,
  shouldPreventDefault = true
) => {
  if (!event) return null;
  // If the keydown event is triggered with either of (Enter or Space) key is pressed,
  // then return true
  if (_.includes(["Enter", "Space"], event.code)) {
    if (shouldPreventDefault) {
      event?.preventDefault();
    }
    cb(event);
  }
};

/**
 * Handles keydown events for Enter or Space keys and triggers a callback function with code mapping.
 *
 * @param {KeyboardEvent} event - The keyboard event object.
 * @param {Function} cb - The callback function to be executed when Enter or Space is pressed.
 * @param {boolean} [shouldPreventDefault=true] - Whether to prevent the default browser behavior.
 * @param {Object} CODE_MAPPING - The mapping of key codes to their corresponding actions.
 */

export const onEnterOrSpaceKeyDownWithCodeMapping = ({
  event,
  cb,
  shouldPreventDefault = true,
  CODE_MAPPING,
}) => {
  if (!event) return null;
  // If the keydown event is triggered with either of (Enter or Space) key is pressed,
  // then return true

  if (_.includes(["Enter", "Space"], event.code) || CODE_MAPPING[event.code]) {
    if (shouldPreventDefault) {
      event?.preventDefault();
    }
    cb(event);
  }
};

/**
 * Returns an array of keyboard focusable elements within the given element.
 *
 * @param {Element} element - The element to search within.
 * @returns {Array<Element>} - An array of keyboard focusable elements.
 */
export const getKeyboardFocusableElements = element => {
  if (_.isNil(element)) return [];

  const focusableElementsQuery = `a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), [contenteditable]:not([tabindex="-1"])`;
  const focusableElements = element?.querySelectorAll(focusableElementsQuery);
  return focusableElements;
};

/**
 * Recursively updates the tabindex attribute of the given element and its focusable descendants.
 *
 * @param {Object} params - The parameters object.
 * @param {HTMLElement} params.element - The root element to start updating tabindex.
 * @param {boolean} params.focusable - A flag indicating whether the elements should be focusable (true) or not (false).
 */
export const updateTabIndexRecursively = ({ element, focusable }) => {
  if (_.isNil(element)) return;

  const value = focusable ? 0 : -1;
  element?.setAttribute("tabindex", value);
  _.forEach(
    _.toArray(
      element?.querySelectorAll(
        "a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([aria-disabled='true']), [contenteditable]"
      )
    ),
    ele => ele?.setAttribute("tabindex", value)
  );
};

/**
 * Finds the next focusable list item based on the keyboard event.
 *
 * @param {Object} params - The parameters object.
 * @param {HTMLElement} params.element - The current focused element.
 * @param {KeyboardEvent} params.event - The keyboard event triggering the focus change.
 * @param {HTMLElement[]} params.listItems - The list of all focusable items.
 * @param {number} params.columns - The number of columns in the list.
 * @returns {HTMLElement|null} The next focusable list item or null if none is found.
 */
const findNextFocusableListItem = ({ element, event, listItems, columns }) => {
  if (_.isNil(element) || _.isNil(event)) return null;

  const currentIndex = _.indexOf(listItems, element);
  const totalItems = _.size(listItems);

  let targetIndex;

  switch (event?.key) {
    case "ArrowDown":
      targetIndex = currentIndex + columns;
      break;
    case "ArrowUp":
      targetIndex = currentIndex - columns;
      break;
    case "ArrowRight":
      targetIndex = currentIndex + 1;
      break;
    case "ArrowLeft":
      targetIndex = currentIndex - 1;
      break;
    default:
      return null;
  }

  if (targetIndex < 0 || targetIndex >= totalItems) {
    return null; // Out of bounds
  }

  const nextTargetElement = listItems[targetIndex];
  if (_.isEqual(nextTargetElement?.getAttribute("aria-disabled"), "true")) {
    return findNextFocusableListItem({
      element: nextTargetElement,
      event,
      listItems,
      columns,
    });
  }

  return nextTargetElement || null;
};

/**
 * Navigates through list items using arrow keys.
 *
 * @param {Object} params - The parameters object.
 * @param {KeyboardEvent} params.event - The keyboard event.
 * @param {HTMLElement} params.element - The current focused element.
 * @param {boolean} [params.shouldPreventDefault=false] - Whether to prevent the default browser behavior.
 * @param {HTMLElement[]} params.listItems - The list of items to navigate through.
 * @param {number} params.columns - The number of columns in the list.
 *
 * @returns {void}
 */
export const navigateThroughArrowKeys = ({
  event,
  element,
  shouldPreventDefault = false,
  listItems,
  columns,
}) => {
  if (_.isNil(event) || _.isNil(element)) return null;

  if (shouldPreventDefault) {
    event?.preventDefault();
  }

  const nextItem = findNextFocusableListItem({
    element,
    event,
    columns,
    listItems,
  });

  if (nextItem) {
    updateTabIndexRecursively({ element, focusable: false }); // Remove focus from current
    nextItem.focus();
    updateTabIndexRecursively({ element: nextItem, focusable: true }); // Focus the next item
  }
};

/**
 * @param {Object} params - The parameters object.
 * @param {KeyboardEvent} params.event - The keyboard event triggered by pressing an arrow key.
 * @param {number} params.index - The current index of the focused item.
 * @param {number} params.columnsCount - The number of columns (or items per row).
 * @param {number} params.itemsCount - The total number of items in the list.
 * @param {Object} params.refList - A ref object containing references to the items.
 * @param {Function} [params.getItemId] - A function to get the item's ID by its index (optional).
 *
 * @description
 * The `navigateGridWithArrowKeys` function allows navigation through a grid of items using the arrow keys.
 * It calculates the new index based on the arrow key pressed (up, down, left, right) and moves the focus
 * to the corresponding item in the `refList`. If the navigation is out of bounds, it won't perform any action.
 *
 * @reference
 * src/AppComponents/UnitSelection/UnitSelectionFeed/UnitSelectionFeed.js
 */

export const navigateGridWithArrowKeys = ({
  event,
  index,
  columnsCount,
  itemsCount,
  refList,
  getItemId,
}) => {
  let newIndex;

  switch (event.key) {
    case "ArrowUp":
      newIndex = index - columnsCount;
      break;

    case "ArrowDown":
      newIndex = index + columnsCount;
      break;

    case "ArrowLeft":
      newIndex = index - 1;
      break;

    case "ArrowRight":
      newIndex = index + 1;
      break;

    default:
      return;
  }

  if (newIndex >= 0 && newIndex < itemsCount) {
    const id = getItemId ? getItemId(newIndex) : newIndex;
    refList.current[id]?.focus();
    event.preventDefault();
  }
};
